import { Button, Calendar, Popover, PopoverContent, PopoverTrigger } from '@ttab/elephant-ui'
import { DuplicatePrompt } from './DuplicatePrompt'
import { addDays, format } from 'date-fns'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import type { HocuspocusProvider } from '@hocuspocus/provider'
import type { Session } from 'next-auth'
import { CopyPlusIcon, CalendarDaysIcon, CalendarPlus2Icon } from '@ttab/elephant-ui/icons'
import { ToastAction } from '@/components/ToastAction'
import type { EventData } from '@/views/Event/components/EventTime'
import type { PlanningData } from '@/types/index'
import { type SetStateAction, type Dispatch } from 'react'
import { useRegistry } from '@/hooks/useRegistry'
import { isEvent, isPlanning } from './isType'
import type { Document } from '@ttab/elephant-api/newsdoc'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

const SingleOrRangedCalendar = ({
  granularity,
  duplicateDate,
  setDuplicateDate,
  dataInfo
}: {
  granularity: 'date' | 'datetime' | undefined
  duplicateDate: { from: Date, to?: Date | undefined }
  setDuplicateDate: Dispatch<SetStateAction<{ from: Date, to?: Date | undefined }>>
  dataInfo: EventData | PlanningData
}) => {
  const { locale } = useRegistry()

  if (isEvent(dataInfo)) {
    if (!granularity) {
      return <></>
    }

    if (granularity === 'date' && duplicateDate?.from) {
      return (
        <Calendar
          mode='range'
          locale={locale.module}
          selected={duplicateDate}
          defaultMonth={duplicateDate.from}
          onSelect={(selectedDate) => {
            if (!selectedDate?.from || !selectedDate?.to) {
              return
            }

            setDuplicateDate({
              from: new Date(selectedDate.from),
              to: new Date(selectedDate?.to)
            })
          }}
        />
      )
    }
  }


  return (
    <Calendar
      mode='single'
      locale={locale.module}
      selected={duplicateDate?.from}
      defaultMonth={duplicateDate.from}
      onSelect={(selectedDate) => {
        if (!selectedDate) {
          return
        }

        setDuplicateDate({ from: new Date(format(selectedDate, 'yyyy-MM-dd')), to: undefined })
      }}
    />
  )
}

export const Duplicate = ({ provider, title, session, status, type, dataInfo }: {
  provider: HocuspocusProvider
  title: string | undefined
  session: Session | null
  status: 'authenticated' | 'loading' | 'unauthenticated'
  type: 'Event' | 'Planning'
  dataInfo: EventData | PlanningData
}) => {
  const granularity = isEvent(dataInfo) ? dataInfo?.dateGranularity : undefined
  const [duplicateDate, setDuplicateDate] = useState<{ from: Date, to?: Date | undefined }>({ from: new Date(), to: new Date() })
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const { locale, repository } = useRegistry()
  const { t } = useTranslation()

  useEffect(() => {
    if (isEvent(dataInfo)) {
      const dates = granularity === 'date'
        ? { from: dataInfo?.start ? new Date(dataInfo?.start) : new Date(), to: dataInfo?.end ? new Date(dataInfo?.end) : new Date() }
        : { from: dataInfo?.start ? new Date(dataInfo?.start) : new Date(), to: dataInfo?.end ? new Date(dataInfo?.end) : undefined }

      setDuplicateDate(dates)
    }

    if (isPlanning(dataInfo)) {
      const dates = {
        from: addDays(new Date(dataInfo.start_date), 1),
        to: addDays(new Date(dataInfo.end_date), 1)
      }
      setDuplicateDate(dates)
    }
  }, [dataInfo, granularity])

  if (!dataInfo) {
    return <></>
  }

  const createTexts = (granularity: 'date' | 'datetime' | undefined, t: TFunction): { description: string, success: string } => {
    const start = format(duplicateDate.from, 'EEEE yyyy-MM-dd', { locale: locale.module })
    const defaultTexts = {
      description: `${t('shared:copy.copyPrompt', { title, start })}`,
      success: `${t('shared:copy.copySuccess', { title, start })}`
    }

    if (isEvent(dataInfo)) {
      if (!granularity) {
        return { description: '', success: '' }
      }

      if (granularity === 'date' && duplicateDate?.to) {
        const end = format(duplicateDate?.to, 'EEEE yyyy-MM-dd', { locale: locale.module })
        const datesFormatted = `${start === end ? `${start}` : `${start} - ${end}`}`

        return {
          description: `${t('shared:copy.copyEventPrompt', { datesFormatted })}`,
          success: `${t('shared:copy.copyEventSuccess', { title, datesFormatted })}`
        }
      }

      if (granularity === 'datetime') {
        return defaultTexts
      }
    }

    return defaultTexts
  }

  return (
    <div className='flex-row gap-2 justify-start items-center'>
      <Popover>
        <PopoverTrigger title={t('common:actions.copy')}>
          <div
            className='flex items-center justify-center w-9 h-9 px-0 hover:bg-gray-200 dark:hover:bg-table-focused'
          >
            <CopyPlusIcon size={20} strokeWidth={1.75} />
          </div>
        </PopoverTrigger>
        <PopoverContent>
          <SingleOrRangedCalendar
            dataInfo={dataInfo}
            granularity={granularity}
            duplicateDate={duplicateDate}
            setDuplicateDate={setDuplicateDate}
          />
          <div className='flex w-full justify-end'>
            <Button onClick={() => setShowConfirm(!showConfirm)}>
              {t('common:actions.copy')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {showConfirm && (
        <DuplicatePrompt
          duplicateDate={duplicateDate}
          provider={provider}
          type={type}
          description={createTexts(granularity, t).description}
          secondaryLabel={t('common:actions.abort')}
          primaryLabel={t('common:actions.copy')}
          onPrimary={async (duplicateId: string | undefined, duplicatedDocument: Document) => {
            if (provider && status === 'authenticated' && duplicateId && session && repository) {
              try {
                await repository.saveDocument(duplicatedDocument, session.accessToken)

                toast.success(createTexts(granularity, t).success, {
                  action: (
                    <ToastAction
                      documentId={duplicateId || undefined}
                      withView={type}
                      Icon={type === 'Event' ? CalendarPlus2Icon : CalendarDaysIcon}
                      label={t('common:actions.openType', { type: type === 'Event' ? t('core:documentType.event') : t('core:documentType.planning') })}
                    />
                  )
                })
              } catch (error) {
                toast.error(`${t('errors:messages.someError')}: ${JSON.stringify(error)}`)
                console.error(error)
              }
            }
            setShowConfirm(false)
          }}
          onSecondary={() => {
            setShowConfirm(false)
          }}
        />
      )}
    </div>
  )
}
